// frontend/src/widgets/OpportunityListWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-01-14
// Updated:      2026-09-09 09:23 BST
//
// Description:
//   Opportunities list widget (left column, bottom).
//
// Mobile Responsiveness (2026-09-09):
//   • Preserves the original clickable opportunity-row div and direct
//     onSelect(o.id) behaviour.
//   • Preserves the original Refresh button behaviour.
//   • Preserves the original mouse-only gold-standard tooltip implementation.
//   • Uses natural widget/list height on mobile while retaining desktop
//     internal scrolling.
//   • Stacks the count / Refresh row on narrow screens.
//   • Keeps the search input full-width and mobile-safe.
//   • Allows opportunity name, stage, status and updated date text to wrap
//     on mobile while retaining desktop ellipsis / nowrap behaviour.
//   • No API, filtering, selection, refresh or tooltip behaviour is changed.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../context/ThemeContext.jsx"; // ✅ FIXED (was ../../...)

// -------------------- Gold-standard Tooltip (portal, unclipped, label-hover, theme primary bg) --------------------
function useGoldTooltip(theme) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const rafRef = useRef(null);
  const openRef = useRef(false);

  const bg = theme?.primary_color || "#111827";

  const hide = () => {
    openRef.current = false;
    setOpen(false);
    setText("");
  };

  const show = (t) => {
    const s = String(t || "").trim();
    if (!s) return;
    openRef.current = true;
    setText(s);
    setOpen(true);
  };

  const move = (clientX, clientY) => {
    if (!openRef.current) return;

    const pad = 12;
    const maxW = 360;

    const vw = window.innerWidth || 1200;
    const vh = window.innerHeight || 800;

    let x = clientX + 14;
    let y = clientY + 14;

    const clampX = vw - maxW - pad;
    if (x > clampX) x = Math.max(pad, vw - maxW - pad);

    const approxH = 72;
    const clampY = vh - approxH - pad;
    if (y > clampY) y = Math.max(pad, vh - approxH - pad);

    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => setPos({ x, y }));
  };

  useEffect(() => {
    const onScroll = () => hide();
    const onKey = (e) => {
      if (e.key === "Escape") hide();
    };
    const onBlur = () => hide();

    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const Tooltip = useMemo(() => {
    if (!open || !text) return null;

    const style = {
      position: "fixed",
      left: pos.x,
      top: pos.y,
      zIndex: 99999,
      maxWidth: 360,
      background: bg,
      color: "#fff",
      padding: "10px 12px",
      borderRadius: 10,
      boxShadow: "0 10px 30px rgba(0,0,0,0.22)",
      fontSize: 12,
      fontWeight: 800,
      lineHeight: 1.25,
      whiteSpace: "pre-line",
      pointerEvents: "none",
    };

    return createPortal(<div style={style}>{text}</div>, document.body);
  }, [open, text, pos.x, pos.y, bg]);

  const bind = (tooltipText) => ({
    onMouseEnter: (e) => {
      show(tooltipText);
      move(e.clientX, e.clientY);
    },
    onMouseMove: (e) => move(e.clientX, e.clientY),
    onMouseLeave: () => hide(),
  });

  return { Tooltip, bind, hide };
}

export default function OpportunityListWidget({
  opportunities = [],
  loading = false,
  error = "",
  selectedId = null,
  onSelect,
  onRefresh,
}) {
  const { theme } = useTheme();
  const { Tooltip, bind } = useGoldTooltip(theme);

  const [q, setQ] = useState("");

  const wrap = useMemo(
    () => ({
      background: theme?.card_bg || "#fff",
      borderRadius: 14,
      padding: 14,
      boxShadow: theme?.card_shadow || "0 1px 2px rgba(0,0,0,0.06)",
      border: theme?.card_border || "1px solid rgba(0,0,0,0.06)",
      display: "flex",
      flexDirection: "column",
      minHeight: 0,
      boxSizing: "border-box",
    }),
    [theme]
  );

  const list = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      gap: 8,
      paddingRight: 4,
      minHeight: 0,
    }),
    []
  );

  const inputStyle = useMemo(
    () => ({
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
      borderRadius: 10,
      border: "1px solid rgba(0,0,0,0.12)",
      padding: "10px 12px",
      fontSize: 12,
      outline: "none",
    }),
    []
  );

  const filtered = useMemo(() => {
    const s = String(q || "").trim().toLowerCase();
    if (!s) return opportunities;
    return (opportunities || []).filter((o) => {
      const name = String(o?.name || "").toLowerCase();
      const stage = String(o?.stage || "").toLowerCase();
      const status = String(o?.status || "").toLowerCase();
      return name.includes(s) || stage.includes(s) || status.includes(s);
    });
  }, [opportunities, q]);

  const row = (active) => ({
    borderRadius: 12,
    padding: 10,
    cursor: "pointer",
    border: active
      ? `2px solid ${theme?.accent_color || "#2563eb"}`
      : "1px solid rgba(0,0,0,0.08)",
    background: active
      ? theme?.accent_soft || "rgba(37,99,235,0.08)"
      : theme?.panel_bg || "#fafafa",
    color: theme?.primary_color || theme?.text_primary || "#111827",
    display: "flex",
    flexDirection: "column",
    gap: 6,
  });

  const nameStyle = useMemo(
    () => ({
      fontWeight: 900,
      fontSize: 13,
      overflowWrap: "anywhere",
    }),
    []
  );

  const stageStyle = useMemo(
    () => ({
      fontSize: 11,
      opacity: 0.75,
      fontWeight: 900,
      overflowWrap: "anywhere",
    }),
    []
  );

  const statusStyle = useMemo(
    () => ({
      fontSize: 12,
      opacity: 0.85,
      fontWeight: 800,
      overflowWrap: "anywhere",
    }),
    []
  );

  const updatedStyle = useMemo(
    () => ({
      fontSize: 11,
      opacity: 0.6,
      fontWeight: 800,
      overflowWrap: "anywhere",
    }),
    []
  );

  const countText = useMemo(() => {
    if (loading) return "Loading…";
    return `Showing ${filtered.length} of ${(opportunities || []).length}`;
  }, [filtered.length, loading, opportunities]);

  const refreshTip =
    "Reload opportunities list from the server.\nUse when you expect new/updated items.";

  return (
    <div
      className="h-auto min-h-0 w-full overflow-visible md:h-full md:overflow-hidden"
      style={wrap}
    >
      {/* No internal heading; wrapper supplies title */}
      <div className="mb-2.5 flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-2.5">
        <div
          className="min-w-0 break-words"
          style={{
            fontSize: 12,
            fontWeight: 900,
            opacity: 0.75,
            overflowWrap: "anywhere",
          }}
        >
          {countText}
        </div>

        {/* Tooltip on button label text (not a question-mark, no native title) */}
        <button
          type="button"
          className="min-h-[44px] w-full bg-btnGeneralNormal text-white hover:bg-btnGeneralHover active:bg-btnGeneralClick sm:w-auto"
          style={{
            borderRadius: 10,
            padding: "8px 10px",
            fontWeight: 900,
            fontSize: 12,
          }}
          onClick={() => onRefresh && onRefresh()}
          {...bind(refreshTip)}
        >
          Refresh
        </button>
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        {/* Tooltip on label text equivalent: hover input itself for guidance */}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search..."
          style={inputStyle}
          {...bind(
            "Search by name, stage, or status.\nMatches are case-insensitive."
          )}
        />
      </div>

      {error ? (
        <div
          className="mt-2.5 break-words"
          style={{
            color: theme?.danger || "#b91c1c",
            fontSize: 12,
            fontWeight: 800,
            overflowWrap: "anywhere",
          }}
        >
          {error}
        </div>
      ) : null}

      {loading ? (
        <div
          className="mt-2.5"
          style={{
            fontSize: 12,
            opacity: 0.75,
            fontWeight: 800,
          }}
        >
          Loading…
        </div>
      ) : null}

      <div
        className="mt-2.5 overflow-visible md:flex-1 md:overflow-y-auto"
        style={list}
      >
        {filtered.map((o) => {
          const active = selectedId === o.id;

          const stage = String(o?.stage || "").toUpperCase();
          const status = String(o?.status || "open").toUpperCase();
          const updated = o?.updated_at
            ? String(o.updated_at).slice(0, 10)
            : "";

          const tip = [
            `Stage: ${stage || "—"}`,
            `Status: ${status || "—"}`,
            updated ? `Updated: ${updated}` : null,
          ]
            .filter(Boolean)
            .join("\n");

          return (
            <div
              key={o.id}
              style={row(active)}
              onClick={() => onSelect && onSelect(o.id)}
            >
              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:justify-between sm:gap-2.5">
                <div
                  className="min-w-0 break-words md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                  style={nameStyle}
                >
                  {o?.name || `Opportunity ${o.id}`}
                </div>

                {/* Tooltip on the stage text */}
                <div
                  className="min-w-0 break-words sm:shrink-0 sm:text-right md:max-w-[45%] md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                  style={stageStyle}
                  {...bind(tip)}
                >
                  {stage}
                </div>
              </div>

              <div className="flex min-w-0 flex-col gap-1 sm:flex-row sm:justify-between sm:gap-2.5">
                {/* Tooltip on status text (same tip) */}
                <div
                  className="min-w-0 break-words md:overflow-hidden md:text-ellipsis md:whitespace-nowrap"
                  style={statusStyle}
                  {...bind(tip)}
                >
                  {status}
                </div>

                <div
                  className="min-w-0 break-words sm:shrink-0 sm:text-right md:whitespace-nowrap"
                  style={updatedStyle}
                >
                  {updated}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {Tooltip}
    </div>
  );
}
